import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import { DeliveryStopPod } from "api/response";
import { BaseSelect } from "components/v2/Select";
import testIds from "constants/testIds";
import { formatDeliveryStopOptionLabel, isStopPodDelivered } from "../formatDeliveryStopOptionLabel";

export type MultistopDeliveryStopSelectProps = Readonly<{
  stops: DeliveryStopPod[];
}>;

export default function MultistopDeliveryStopSelect({ stops }: MultistopDeliveryStopSelectProps) {
  const intl = useIntl();
  const { control, watch } = useFormContext();
  const selectedStopId = watch("stop_id");

  const options = useMemo(
    () =>
      stops.map((stop) => ({
        value: String(stop.stop_id),
        label: formatDeliveryStopOptionLabel(stop),
        isDisabled: isStopPodDelivered(stop),
      })),
    [stops],
  );

  const selectedStop = stops.find((stop) => stop.stop_id === Number(selectedStopId));

  return (
    <div data-testid={testIds.SHIPMENT_DASHBOARD_MULTISTOP_POD_STOP_SELECT}>
      <Controller
        name="stop_id"
        control={control}
        shouldUnregister
        rules={{
          validate: (value) =>
            value != null ||
            intl.formatMessage({
              id: "general.requiredField",
              defaultMessage: "This is a required field",
            }),
        }}
        render={({ field, fieldState }) => (
          <BaseSelect
            name="stop_id"
            label={intl.formatMessage({
              id: "components.molecules.top-nav.deliveryLocation",
              defaultMessage: "Delivery location",
            })}
            required
            options={options}
            value={field.value != null ? String(field.value) : null}
            onChange={(value) => field.onChange(value ? Number(value) : null)}
            onBlur={field.onBlur}
            hintText={selectedStop?.full_address}
            error={fieldState.error ?? null}
          />
        )}
      />
    </div>
  );
}
